import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toastError } from '../../../shared/toastHelper';
import { EventCard, DateGroup, STATUS_COLORS } from './MobileEventList';
import MobileCalendarDropdown, { buildYearOptions } from './MobileCalendarDropdown';
import {
  SUNDAY_WEEKDAYS as WEEKDAYS,
  MONTH_NAMES,
  getDisplayRange,
  toISODateString,
  isSameDay,
  savedMonthStart,
  useSaveMonthOnChange,
  useGoToDate
} from './mobileCalendarHelpers';
import '../styles/holdChallenges.css';

const challengeStatus = (dayEvents) => {
  const challenges = dayEvents.flatMap((event) => event.challenges || []);
  if (challenges.length === 0) return null;
  return challenges.some((challenge) => challenge.overdue) ? 'overdue' : 'open';
};

const MobileMonthCalendar = ({
  team,
  dataSource,
  filters,
  searchTerm,
  selectedRooms,
  goToTodaySignal,
  goToDate,
  refreshSignal,
  initialMonth,
  onMonthChange
}) => {
  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState(() => savedMonthStart(initialMonth, today));
  const opensOnCurrentMonth =
    currentMonth.getFullYear() === today.getFullYear() && currentMonth.getMonth() === today.getMonth();
  const [selectedDate, setSelectedDate] = useState(() => (opensOnCurrentMonth ? today : currentMonth));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const pendingMonthNav = useRef(!opensOnCurrentMonth);
  useSaveMonthOnChange(currentMonth, onMonthChange);

  const isSearchActive = useMemo(
    () => searchTerm && searchTerm.trim().length > 0,
    [searchTerm]
  );

  const { displayStart, displayEnd, monthStart, monthEnd } = useMemo(
    () => getDisplayRange(currentMonth),
    [currentMonth]
  );

  const latestRequestId = useRef(0);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const selectedRoomIds = useMemo(
    () => (selectedRooms || []).map(r => r.value),
    [selectedRooms]
  );
  const selectedRoomIdsKey = selectedRoomIds.join(',');

  const fetchEvents = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++latestRequestId.current;
    const roomIds = filters.room_ids || selectedRoomIds;
    if (roomIds.length === 0) {
      setEvents([]);
      setLoading(false);
      return;
    }

    if (!silent) setLoading(true);

    try {
      const params = {
        ...filters,
        room_ids: roomIds,
        start: 0,
        length: 500,
        ...(isSearchActive ? {} : {
          start_date: toISODateString(displayStart),
          end_date: toISODateString(displayEnd),
        }),
        'search[value]': searchTerm || '',
        mobile: true
      };

      const mobileUrl = dataSource.replace('/datatable', '/mobile_datatable');
      const response = await axios.get(mobileUrl, {
        params,
        headers: { 'Accept': 'application/json' }
      });
      if (requestId !== latestRequestId.current) return;

      const newEvents = response.data.data || [];
      setEvents(newEvents);

      if (pendingMonthNav.current) {
        pendingMonthNav.current = false;
        const monthPrefix = toISODateString(currentMonth).slice(0, 7);
        const firstEventDate = newEvents
          .map(e => e.start_date_iso)
          .filter(d => d && d.startsWith(monthPrefix))
          .sort()[0];

        if (firstEventDate) {
          const [y, m, d] = firstEventDate.split('-').map(Number);
          setSelectedDate(new Date(y, m - 1, d));
        }
      }
    } catch (error) {
      if (requestId !== latestRequestId.current) return;
      console.error('Error fetching month events:', error);
      toastError('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, filtersKey, selectedRoomIdsKey, searchTerm, displayStart, displayEnd, currentMonth, isSearchActive]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (refreshSignal) fetchEvents({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  useGoToDate(goToDate, (date) => {
    setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setSelectedDate(date);
    pendingMonthNav.current = false;
  });

  const eventsByDate = useMemo(() => {
    const grouped = {};
    events.forEach(event => {
      const key = event.start_date_iso;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(event);
    });
    return grouped;
  }, [events]);

  useEffect(() => {
    if (!goToTodaySignal) return;
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(now);
    pendingMonthNav.current = false;
  }, [goToTodaySignal]);

  const jumpToMonth = (newMonth) => {
    setCurrentMonth(newMonth);

    const nowDate = new Date();
    const isCurrentMonth =
      newMonth.getFullYear() === nowDate.getFullYear() && newMonth.getMonth() === nowDate.getMonth();
    pendingMonthNav.current = !isCurrentMonth;

    if (isCurrentMonth) {
      setSelectedDate(nowDate);
    } else {
      setSelectedDate(new Date(newMonth.getFullYear(), newMonth.getMonth(), 1));
    }
  };

  const navigateMonth = (direction) => {
    jumpToMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const handleMonthSelect = (monthIdx) => {
    jumpToMonth(new Date(currentMonth.getFullYear(), monthIdx, 1));
  };

  const handleYearSelect = (year) => {
    jumpToMonth(new Date(year, currentMonth.getMonth(), 1));
  };

  const monthOptions = useMemo(
    () => MONTH_NAMES.map((name, idx) => ({ value: idx, label: name })),
    []
  );

  const yearOptions = useMemo(() => buildYearOptions(today.getFullYear()), [today]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const calendarDays = useMemo(() => {
    const days = [];
    const cursor = new Date(displayStart);
    while (cursor <= displayEnd) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [displayStart, displayEnd]);

  const selectedDateKey = toISODateString(selectedDate);
  const selectedDayEvents = eventsByDate[selectedDateKey] || [];
  const sortedEventDates = useMemo(() => Object.keys(eventsByDate).sort(), [eventsByDate]);

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        border: '1px solid var(--od-c-d5d7da, #D5D7DA)',
        borderRadius: '8px',
        margin: '12px 4px 8px',
        overflow: 'hidden',
        backgroundColor: 'var(--od-c-ffffff, #fff)',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)'
      }}>
        <button
          onClick={() => navigateMonth(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            borderRight: '1px solid var(--od-c-d5d7da, #D5D7DA)',
            padding: '8px 12px',
            minHeight: '40px',
            cursor: 'pointer',
            flexShrink: 0
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" style={{ stroke: 'var(--od-c-414651, #414651)' }} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '4px 8px',
          minHeight: '40px',
          borderRight: '1px solid var(--od-c-d5d7da, #D5D7DA)'
        }}>
          <MobileCalendarDropdown
            label={MONTH_NAMES[currentMonth.getMonth()]}
            ariaLabel="Select month"
            options={monthOptions}
            selectedValue={currentMonth.getMonth()}
            onSelect={handleMonthSelect}
          />
          <MobileCalendarDropdown
            label={String(currentMonth.getFullYear())}
            ariaLabel="Select year"
            options={yearOptions}
            selectedValue={currentMonth.getFullYear()}
            onSelect={handleYearSelect}
            width="110px"
          />
        </div>
        <button
          onClick={() => navigateMonth(1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            padding: '8px 12px',
            minHeight: '40px',
            cursor: 'pointer',
            flexShrink: 0
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4.16666 10H15.8333M15.8333 10L9.99999 4.16667M15.8333 10L9.99999 15.8333" style={{ stroke: 'var(--od-c-414651, #414651)' }} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {!isSearchActive && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            textAlign: 'center',
            padding: '4px 0',
            borderBottom: '1px solid var(--od-c-ececec, #ECECEC)'
          }}>
            {WEEKDAYS.map(day => (
              <div key={day} style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--od-c-717680, #717680)',
                padding: '4px 0'
              }}>
                {day}
              </div>
            ))}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '0'
          }}>
            {calendarDays.map((day) => {
              const dateKey = toISODateString(day);
              const dayEvents = eventsByDate[dateKey] || [];
              const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
              const isToday = isSameDay(day, today);
              const isSelected = isSameDay(day, selectedDate);

              const dotColors = [];
              const seenColors = new Set();
              for (const evt of dayEvents) {
                const color = evt.venue_color || STATUS_COLORS[evt.classification] || 'var(--od-c-969696, #969696)';
                if (!seenColors.has(color)) {
                  seenColors.add(color);
                  dotColors.push(color);
                  if (dotColors.length >= 3) break;
                }
              }
              const overflow = dayEvents.length > 3 ? dayEvents.length - 3 : 0;
              const dayChallengeStatus = challengeStatus(dayEvents);

              return (
                <div
                  key={dateKey}
                  onClick={() => handleDateSelect(day)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '6px 2px',
                    minHeight: '52px',
                    cursor: 'pointer',
                    opacity: isCurrentMonth ? 1 : 0.3,
                    backgroundColor: isSelected ? 'var(--od-c-f3f4f6, #F3F4F6)' : 'transparent',
                    borderRadius: isSelected ? '8px' : '0'
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: isToday ? '#2563EB' : 'transparent',
                    color: isToday ? '#fff' : 'var(--od-c-1a1a1a, #1a1a1a)',
                    fontSize: '13px',
                    fontWeight: isToday ? '700' : '400',
                    marginBottom: '2px'
                  }}>
                    {day.getDate()}
                  </div>
                  {dayEvents.length > 0 && (
                    <div style={{
                      display: 'flex',
                      gap: '2px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'nowrap'
                    }}>
                      {dotColors.map((color, i) => (
                        <div
                          key={i}
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: color
                          }}
                        />
                      ))}
                      {overflow > 0 && (
                        <span style={{
                          fontSize: '8px',
                          color: 'var(--od-c-717680, #717680)',
                          fontWeight: '600',
                          lineHeight: '1'
                        }}>
                          +{overflow}
                        </span>
                      )}
                      {dayChallengeStatus && (
                        <i className={`fas fa-bolt mobile-month-challenge-indicator hold-challenge-${dayChallengeStatus}`}
                           title="Hold challenge in progress"
                           data-testid="month-challenge-indicator" />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{
        borderTop: '1px solid var(--od-c-ececec, #ECECEC)',
        marginTop: '4px',
        paddingTop: '12px'
      }}>
        {loading ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: 'var(--od-c-717680, #717680)'
          }}>
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : isSearchActive ? (
          events.length === 0 ? (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: 'var(--od-c-717680, #717680)',
              fontSize: '14px'
            }}>
              No events found
            </div>
          ) : (
            sortedEventDates.map(dateKey => (
              <DateGroup
                key={dateKey}
                date={dateKey}
                events={eventsByDate[dateKey]}
                team={team}
              />
            ))
          )
        ) : (
          <>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--od-c-1a1a1a, #1a1a1a)',
              marginBottom: '12px',
              paddingLeft: '4px'
            }}>
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
            </h3>

            {selectedDayEvents.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: 'var(--od-c-717680, #717680)',
                fontSize: '14px'
              }}>
                {isSameDay(selectedDate, today) ? 'No events on the calendar for today.' : 'No events are on the calendar.'}
              </div>
            ) : (
              selectedDayEvents.map((event, index) => (
                <EventCard
                  key={event.id || index}
                  event={event}
                  team={team}
                  prevClassification={index > 0 ? selectedDayEvents[index - 1].classification : null}
                  dayEvents={selectedDayEvents}
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

MobileMonthCalendar.propTypes = {
  team: PropTypes.object.isRequired,
  dataSource: PropTypes.string.isRequired,
  filters: PropTypes.object,
  searchTerm: PropTypes.string,
  selectedRooms: PropTypes.array,
  goToTodaySignal: PropTypes.number,
  goToDate: PropTypes.shape({ date: PropTypes.string.isRequired }),
  refreshSignal: PropTypes.number,
  initialMonth: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onMonthChange: PropTypes.func
};

MobileMonthCalendar.defaultProps = {
  filters: {},
  searchTerm: '',
  selectedRooms: [],
  goToTodaySignal: 0,
  goToDate: null,
  refreshSignal: 0
};

export default MobileMonthCalendar;
